export const SORT_IDS = new Set([
  'minOrderAmount',
  'avgRating',
  'priceAsc',
  'priceDesc',
  'inStockFirst',
]);

export const slugify = (value) => String(value ?? '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '');

export const CATEGORY_PREFIX = 'cat:';

export const categoryIdFromName = (name) => `${CATEGORY_PREFIX}${slugify(name)}`;

export const partitionFilters = (applied = []) => {
  const sortBy = [];
  const categorySlugs = new Set();
  applied.forEach((id) => {
    if (typeof id !== 'string') {
      return;
    }
    if (id.startsWith(CATEGORY_PREFIX)) {
      categorySlugs.add(id.slice(CATEGORY_PREFIX.length));
    } else if (SORT_IDS.has(id)) {
      sortBy.push(id);
    }
  });
  return { sortBy: sortBy[0] || null, categorySlugs };
};

const normalizeCategoryList = (product, lang) => {
  const preferFr = typeof lang === 'string' && lang.startsWith('fr');
  const arr = preferFr
    ? product?.categories?.frNames
    : product?.categories?.enNames;
  if (Array.isArray(arr) && arr.length > 0) {
    return arr;
  }
  const fallback = preferFr
    ? product?.categories?.enNames
    : product?.categories?.frNames;
  return Array.isArray(fallback) ? fallback : [];
};

export const collectProductCategories = (products = [], lang = 'en') => {
  const seen = new Map();
  products.forEach((p) => {
    normalizeCategoryList(p, lang).forEach((name) => {
      if (typeof name !== 'string') {
        return;
      }
      const label = name.trim();
      if (!label) {
        return;
      }
      const slug = slugify(label);
      if (!seen.has(slug)) {
        seen.set(slug, label);
      }
    });
  });
  return [...seen.values()];
};

export const collectMerchantCategories = (merchants = []) => {
  const seen = new Map();
  merchants.forEach((m) => {
    const cats = m?.marketplaceCategories;
    if (!Array.isArray(cats)) {
      return;
    }
    cats.forEach((name) => {
      if (typeof name !== 'string') {
        return;
      }
      const label = name.trim();
      if (!label) {
        return;
      }
      const slug = slugify(label);
      if (!seen.has(slug)) {
        seen.set(slug, label);
      }
    });
  });
  return [...seen.values()];
};

export const productMatchesCategorySlugs = (product, categorySlugs, lang = 'en') => {
  if (!categorySlugs || categorySlugs.size === 0) {
    return true;
  }
  const names = normalizeCategoryList(product, lang);
  return names.some((name) => categorySlugs.has(slugify(name)));
};

export const merchantMatchesCategorySlugs = (merchant, categorySlugs) => {
  if (!categorySlugs || categorySlugs.size === 0) {
    return true;
  }
  const cats = merchant?.marketplaceCategories;
  if (!Array.isArray(cats)) {
    return false;
  }
  return cats.some((name) => typeof name === 'string' && categorySlugs.has(slugify(name)));
};

export const compareProductsBySort = (sortBy) => (a, b) => {
  if (sortBy === 'priceAsc') {
    const pa = Number(a?.price ?? Infinity);
    const pb = Number(b?.price ?? Infinity);
    return pa - pb;
  }
  if (sortBy === 'priceDesc') {
    const pa = Number(a?.price ?? -Infinity);
    const pb = Number(b?.price ?? -Infinity);
    return pb - pa;
  }
  if (sortBy === 'inStockFirst') {
    const aIn = a?.availability === 'in-stock' ? 0 : 1;
    const bIn = b?.availability === 'in-stock' ? 0 : 1;
    return aIn - bIn;
  }
  return 0;
};

export const applyProductFilters = (products = [], applied = [], lang = 'en') => {
  const { sortBy, categorySlugs } = partitionFilters(applied);
  const filtered = products.filter((p) => productMatchesCategorySlugs(p, categorySlugs, lang));
  if (!sortBy || !['priceAsc', 'priceDesc', 'inStockFirst'].includes(sortBy)) {
    return filtered;
  }
  return [...filtered].sort(compareProductsBySort(sortBy));
};
